
import { defineComponent } from "vue";

import StatusCard from "./StatusCard.vue";
import SubscriptionsSkeleton from "./SubscriptionsSkeleton.vue";
import { CheckoutModal } from "../modals";
import {
  Quote,
  QuoteRequestType,
} from "functions/src/Payments/PaymentsInterfaces";
import {
  QuoteSubscriptionRequest,
  subscriptionType,
} from "functions/src/Subscriptions/SubscriptionsInterfaces";
import {
  quoteSubscription,
  refreshUserPackageStatus,
} from "@/store/Subscriptions/SubscriptionsConsts";
import { mapGetters } from "vuex";
import * as bs from "bootstrap";
import { UserSubscription } from "functions/src/Users/UsersInterfaces";
import {
  normalizePackageLabel,
  PKG_FLY,
  PKG_GO,
  PKG_WIN,
  SUB_ACADEMY_NEW,
  SUB_LITE_NEW,
  SUB_LICENSE_GLOBAL,
} from "functions/src/Subscriptions/packageIds";
// ⏳ TEMPORAL (#610) — se va con el corte, igual que los dos usos del backend.
// SE IMPORTA, NO SE COPIA: el texto y el discriminante son los mismos que aplica
// `QuoteSubscription`, y una copia en el front sería una copia que un día dice
// una cosa mientras el servidor hace otra. El módulo es una HOJA (su único
// import es un TIPO de firebase-admin, que el compilador borra), así que no
// arrastra nada del backend al bundle.
import {
  DORMANT_SELF_MESSAGE,
  isDormantUnplaced,
  SUPPORT_EMAIL,
} from "functions/src/System/DormantReactivationGate";
// `GtrVip` y `GtrLite` ya no se importan aquí: sus cards de compra dejaron de
// pintarse el 2026-07-24. Los FICHEROS siguen en `buy-cards/` — volver a
// enseñarlas es devolver su bloque y su import, no recuperar un componente.
// `GtrBasic` sí se queda: pinta el upgrade de Go a Fly.
import {
  GtrVipToLite,
  GtrBasic,
  MmiColombia,
  GtrBasicToLite,
  GtrGo,
  GtrFly,
  GtrWin,
} from "./buy-cards";

export default defineComponent({
  name: "subscriptions-panel",
  components: {
    StatusCard,
    SubscriptionsSkeleton,
    CheckoutModal,
    GtrVipToLite,
    GtrBasic,
    GtrBasicToLite,
    MmiColombia,
    GtrGo,
    GtrFly,
    GtrWin,
  },
  data() {
    return {
      affiliateIsPaid: false,
      isQuoting: false,
      isRetrying: false,
      quote: {} as Quote<QuoteRequestType>,
      modalObj: {} as any,
      popovers: [] as any,
    };
  },
  computed: {
    ...mapGetters("auth", ["getUsernamePK", "getUserProfile"]),
    ...mapGetters("subs", [
      "getUserSubscription",
      "getSubscriptionConfig",
      "getUserCurrentPackage",
      "getUserPackageStatus",
      "isUserActive",
    ]),
    ...mapGetters("sys", ["isOffline"]),
    /**
     * ⏳ TEMPORAL (#610) — ¿el que está mirando esta página es un dormido sin
     * colocar?
     *
     * NO CUESTA NINGUNA LECTURA NUEVA. `doGetUserProfile` (`AuthStore.ts` :160)
     * ya se trae el documento `users/{u}` ENTERO al iniciar sesión y lo mantiene
     * vivo con un `onSnapshot`, así que `enroller` está en memoria desde antes
     * de que esta pantalla exista. Y como es un listener, el día que soporte
     * coloque a esta persona la tarjeta desaparece sola, sin recargar.
     *
     * EL `_usernamePK` NO ES DECORACIÓN. Al cerrar sesión el store deja el
     * perfil en `{}`, y un objeto vacío no tiene `enroller` — o sea que
     * `isDormantUnplaced({})` es `true`. Sin esta comprobación, cualquier
     * fotograma en que el perfil esté a medias pintaría la tarjeta del dormido a
     * alguien que no lo es. Con perfil sin cargar (`undefined`) la respuesta ya
     * es `false` por sí sola, que es la correcta: mientras no se sepa, tienda.
     */
    isDormantUnplacedSelf(): boolean {
      const profile = this.getUserProfile;
      return Boolean(profile?._usernamePK) && isDormantUnplaced(profile);
    },
    /** El texto de la tarjeta, tal cual lo escribió Hector. No se edita aquí. */
    dormantSelfMessage(): string {
      return DORMANT_SELF_MESSAGE;
    },
    supportEmail(): string {
      return SUPPORT_EMAIL;
    },
    // El nivel del usuario, traducido una vez y preguntado por nombre desde el
    // template: así ninguna condición de la plantilla compara literales sueltos.
    isLevelGo(): boolean {
      return normalizePackageLabel(this.getUserCurrentPackage) === PKG_GO;
    },
    isLevelFly(): boolean {
      return normalizePackageLabel(this.getUserCurrentPackage) === PKG_FLY;
    },
    isLevelWin(): boolean {
      return normalizePackageLabel(this.getUserCurrentPackage) === PKG_WIN;
    },
    academySubsData(): UserSubscription {
      return this.getUserSubscription("trading-academy");
    },
    /**
     * El id que la tarjeta de paquete enseña y RENUEVA.
     *
     * El NIVEL decide de qué FAMILIA se habla —academia o lite—; cuál de las dos
     * generaciones se renueva lo decide EL DATO: la que el usuario ya tiene
     * gobernando. Gemela exacta de `licenseSubscriptionId` de aquí abajo, y por
     * la misma razón: desde que las herramientas de `/tools` venden `gtr-fly`,
     * hay gente cuyo único documento de academia es el NUEVO. Devolviendo el
     * literal viejo a secas, su renovación abriría un SEGUNDO documento de
     * academia al lado del que ya paga.
     *
     * `getUserSubscription` resuelve la familia (las dos generaciones) y
     * devuelve la de `endDate` mayor; cuando no hay ninguna devuelve un objeto
     * sintético con el id que se le pidió, así que el de siempre es también el
     * fallback — que es lo que tienen los ~41,5k dormidos.
     */
    academySubscriptionId(): subscriptionType {
      // 🔴 EL FALLBACK ES EL ID NUEVO (Hector, 2026-07-27). Antes eran los
      // legacy —`trading-academy` / `gtr-lite`—, y eso hacía que QUIEN NO TIENE
      // NINGÚN DOCUMENTO estrenara el vocabulario RETIRADO: comprabas Fly y se
      // te creaba `trading-academy`. Es el caso de los ~41,5k barridos y de toda
      // alta nueva, o sea el mayoritario después del reinicio.
      //
      // No rompe la compatibilidad que este bloque vino a dar: `subscriptionFamilyOf`
      // empareja por PERTENENCIA a la familia y cada familia lleva las DOS
      // generaciones (`[trading-academy, gtr-fly]`, `[gtr-lite, gtr-go]`), así que
      // `getUserSubscription` sigue encontrando el documento viejo de quien lo
      // tenga y lo renueva. Lo único que cambia es de qué id se parte cuando no
      // hay ninguno.
      const paired: subscriptionType = (() => {
        switch (normalizePackageLabel(this.getUserCurrentPackage)) {
          case PKG_WIN:
          case PKG_FLY:
            return SUB_ACADEMY_NEW as subscriptionType;
          case PKG_GO:
            return SUB_LITE_NEW as subscriptionType;
          default:
            return SUB_ACADEMY_NEW as subscriptionType;
        }
      })();
      // SE DEVUELVE EL NUEVO Y PUNTO. Aquí había un `getUserSubscription(paired)`
      // que, si la persona tenía un documento VIEJO con `endDate` mayor, devolvía
      // EL VIEJO y lo renovaba. Fuera (Hector, 2026-07-27): los paquetes antiguos
      // DESAPARECEN, ya sólo existen como historia, y el paso `translate-subs--c`
      // del reinicio traduce los documentos que había. Renovarlos habría vuelto a
      // escribir vocabulario retirado en una base ya traducida.
      return paired;
    },
    /**
     * El id que la tarjeta de licencia enseña y RENUEVA.
     *
     * EL EMPAREJAMIENTO SE QUEDA EN PIE (es el `switch` de abajo, intacto): a la
     * academia su licencia, al Lite la suya. Lo único que se añade es que, si el
     * usuario YA lleva la GLOBAL y es la que gobierna, se renueve ÉSA y no se le
     * abra un segundo documento de licencia al lado.
     *
     * `getUserSubscription` resuelve la familia (la suya ∪ la global) y devuelve
     * la de `endDate` mayor; cuando no hay ninguna devuelve un objeto sintético
     * con el id que se le pidió, así que el emparejado es también el fallback.
     */
    licenseSubscriptionId(): subscriptionType {
      // 🔴 EL FALLBACK ES LA LICENCIA GLOBAL (Hector, 2026-07-27), por el mismo
      // motivo que el bloque de arriba: quien NO TIENE NINGUNA licencia estrenaba
      // el id retirado —`distributor-license` o `lite-distributor-license`—, y ése
      // es el caso de los ~41,5k barridos y de toda alta nueva.
      //
      // EL EMPAREJAMIENTO SIGUE EN PIE para quien YA tiene la suya: la global está
      // dentro de LAS DOS familias (`ACADEMY_LICENSE_IDS` y `LITE_LICENSE_IDS`),
      // así que `getUserSubscription` encuentra y renueva la licencia existente,
      // sea la de academia o la de Lite. Sólo cambia de dónde se parte cuando no
      // hay ninguna.
      // LA LICENCIA ES UNA SOLA. Aquí había un `switch` que emparejaba academia
      // con `distributor-license` y Lite con `lite-distributor-license`, y luego
      // un `getUserSubscription` que renovaba la vieja si la tenía. Las dos cosas
      // fuera (Hector, 2026-07-27): da igual cuál tuviera antes, ya sólo existe
      // `gtr-distributor-license`. Las viejas quedan como historia.
      return SUB_LICENSE_GLOBAL as subscriptionType;
    },
    licenseSubsData(): UserSubscription {
      return this.getUserSubscription(this.licenseSubscriptionId);
    },
    academyIsActive(): boolean {
      return this.academySubsData.isActive
        ? this.academySubsData.isActive
        : false;
    },
    licenseIsActive(): boolean {
      return this.licenseSubsData.isActive
        ? this.licenseSubsData.isActive
        : false;
    },
    isActive(): {
      academy: boolean;
      distributor: boolean;
      mmiColombia: boolean;
    } {
      return {
        academy: this.academyIsActive,
        distributor: this.licenseIsActive,
        mmiColombia: false, //this.licenseIsActive
      };
    },
  },
  methods: {
    /**
     * Vuelve a preguntar QUÉ TIENE el usuario, sin tocar el listener.
     *
     * La acción es la misma que usa el listener de Firestore cuando llega una
     * instantánea, así que no hay dos caminos que puedan divergir. Y no se
     * vuelve a lanzar `doGetUserSubscriptions`: eso abriría un SEGUNDO
     * `onSnapshot` y el primero se quedaría escuchando sin que nadie lo pueda
     * cerrar, porque el store guarda un solo cancelador.
     */
    async retryPackageStatus() {
      this.isRetrying = true;
      try {
        await this.$store.dispatch("subs/" + refreshUserPackageStatus);
      } finally {
        this.isRetrying = false;
      }
    },
    clearQuote() {
      this.quote = {} as Quote<QuoteRequestType>;
    },
    createQuote(request: QuoteSubscriptionRequest) {
      if (!this.isOffline) {
        this.isQuoting = true;
        this.$store.dispatch("subs/" + quoteSubscription, request).then((q) => {
          if (q.OPCODE == "ERROR") {
            alert(q.message);
          } else {
            this.quote = q;

            if (!this.modalObj._isShown) {
              this.modalObj.show();
            }
          }
          this.isQuoting = false;
        });
      }
    },
    paid() {
      this.affiliateIsPaid = true;
    },
    affiliateMarkedAsPaid() {
      this.affiliateIsPaid = false;
    },
  },
  mounted() {
    this.modalObj = new bs.Modal(document.getElementById("checkout-modal"));
  },
});
